export const trimToUndefined = (value?: string | null): string | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined
  }
  if (typeof value !== 'string') {
    return value
  }
  return value.trim().length === 0 ? undefined : value.trim()
}

export function isEmpty(value?: string | null): value is undefined {
  if (value === null || value === undefined) {
    return true
  }
  return !trimToUndefined(value.toString())
}

export function isNotEmpty(value?: string | null): value is string {
  return !isEmpty(value)
}

export function upperFirst(value?: string | null) {
  if (value === null || value === undefined) {
    return undefined
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export const normalizeFormValue = <T>(value: T | null | undefined): T | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return undefined
  }
  return value
}

export const mergeNonEmptyStrings = (
  strings: (string | undefined | number | null | unknown)[],
  joinChar = ' ',
): string | undefined => {
  const nonEmptyStrings = strings //
    .filter(string => {
      if (typeof string === 'number') {
        return true
      }
      return typeof string === 'string' && (string.trim().length ?? 0) > 0
    })
  return nonEmptyStrings.length === 0 ? undefined : nonEmptyStrings.join(joinChar)
}

interface AddressLike {
  streetAddress?: string | null
  zipCode?: string | null
  city?: string | null
}

export const formatAddress = (
  name: string | (string | undefined | null | unknown)[] | undefined,
  address: AddressLike | null | unknown = {},
  defaultValue = '',
  separator = ', ',
): string => {
  const firstLine = Array.isArray(name) ? mergeNonEmptyStrings(name as any, ' ') : name
  const secondLine = mergeNonEmptyStrings(
    [(address as any)?.streetAddress, mergeNonEmptyStrings([(address as any)?.zipCode, (address as any)?.city])],
    separator,
  )
  return mergeNonEmptyStrings([firstLine, secondLine], ', ') || defaultValue
}

export const formatAddressMultiLine = (
  name: string | (string | undefined | null | unknown)[] | undefined,
  address: AddressLike | null | unknown = {},
  defaultValue = '',
  separator = ' ',
): string => {
  const firstLine = Array.isArray(name) ? mergeNonEmptyStrings(name as any, separator) : name
  const secondLine = mergeNonEmptyStrings(
    [(address as any)?.streetAddress, mergeNonEmptyStrings([(address as any)?.zipCode, (address as any)?.city])],
    ' ',
  )
  return mergeNonEmptyStrings([firstLine, secondLine], '\n') || defaultValue
}

export const formatUserInitials = (val?: string) => {
  return val
    ?.split(/[\s.]/)
    ?.map(part => part.charAt(0))
    ?.join('')
    ?.toUpperCase()
}

const norm = (s?: string) => (s ?? '').toString().trim()

export const slugify = (s?: string) =>
  norm(s)
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

export const capitalizeName = (name: string) =>
  name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const string = {
  trimToUndefined,
  mergeNonEmptyStrings,
  formatAddress,
}

export const normalizeWhitespace = (s?: string | null): string => (s ?? '').replace(/\s+/g, ' ').trim()

export const splitLines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map(l => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)

export const prettyResult = (value: any): string => {
  if (value == null) {
    return ''
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch (_e) {
    try {
      return String(value)
    } catch {
      return ''
    }
  }
}

export const getLinesCount = (val?: string | null) => {
  if (!val) {
    return 0
  }
  return val.split('\n').length
}

export const isAnyLineLongerThan = (val: string | undefined | null, length: number) => {
  if (!val) {
    return false
  }
  return val.split('\n').some(r => r.length > length)
}
